import { Card, Col, Flex, Row, Tooltip, Typography, theme } from "antd";
import { format, formatDistanceToNow } from "date-fns";
import { useState } from "react";
import {
	isHealthSignal,
	type Signal,
	SignalKind,
	type SignalState,
	type SignalStatus,
} from "@/common/model";
import { BaseContainer } from "@/components/base";
import { LoadingBar } from "@/components/loading-bar";
import { SignalStateBadge } from "./signal-badges";
import { deltaTone, formatDelta, formatMetric } from "./signal-format";

const SPARK_WIDTH = 160;
const SPARK_HEIGHT = 36;
const SPARK_PAD = 4;

/**
 * The metric's recent values as a line: one series, so no legend; drawn in a de-emphasised ink
 * with the latest point marked in the accent. Hovering a point shows its value. The points come
 * from the source on every read and are never stored.
 */
export function Sparkline({
	series,
	unit,
	label,
}: {
	series: number[];
	unit?: string | null;
	label: string;
}) {
	const { token } = theme.useToken();
	const [hover, setHover] = useState<number | null>(null);
	if (series.length < 2) return null;

	const min = Math.min(...series);
	const max = Math.max(...series);
	const span = max - min || 1;
	const step = (SPARK_WIDTH - SPARK_PAD * 2) / (series.length - 1);
	const x = (i: number) => SPARK_PAD + i * step;
	const y = (v: number) =>
		SPARK_PAD + (SPARK_HEIGHT - SPARK_PAD * 2) * (1 - (v - min) / span);
	const path = series
		.map(
			(v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`,
		)
		.join(" ");
	const last = series.length - 1;

	return (
		<Tooltip
			title={hover == null ? undefined : formatMetric(series[hover], unit)}
			open={hover != null}
		>
			<svg
				viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
				width="100%"
				height={SPARK_HEIGHT}
				role="img"
				aria-label={`${label}: recent values, latest ${formatMetric(series[last], unit)}`}
				// The whole height is the hit target: the nearest point to the pointer's x.
				onMouseMove={(e) => {
					const box = e.currentTarget.getBoundingClientRect();
					const px = ((e.clientX - box.left) / box.width) * SPARK_WIDTH;
					setHover(
						Math.min(last, Math.max(0, Math.round((px - SPARK_PAD) / step))),
					);
				}}
				onMouseLeave={() => setHover(null)}
			>
				<path
					d={path}
					fill="none"
					stroke={token.colorTextQuaternary}
					strokeWidth={2}
					strokeLinejoin="round"
					strokeLinecap="round"
				/>
				{hover != null && hover !== last && (
					<circle
						cx={x(hover)}
						cy={y(series[hover])}
						r={3}
						fill={token.colorTextTertiary}
						stroke={token.colorBgContainer}
						strokeWidth={2}
					/>
				)}
				<circle
					cx={x(last)}
					cy={y(series[last])}
					r={4}
					fill={token.colorPrimary}
					stroke={token.colorBgContainer}
					strokeWidth={2}
				/>
			</svg>
		</Tooltip>
	);
}

/** One metric as a stat tile: value, change against the source's comparison, trend, and state. */
function KeyNumberTile({
	signal,
	status,
	isFetching,
	onOpen,
}: {
	signal: Signal;
	status?: SignalStatus;
	isFetching: boolean;
	onOpen: () => void;
}) {
	const { token } = theme.useToken();
	const delta = formatDelta(status?.value, status?.previousValue);
	const tone = deltaTone(
		status?.value,
		status?.previousValue,
		signal.thresholds?.direction,
	);
	const toneColor = {
		good: token.colorSuccessText,
		bad: token.colorErrorText,
		neutral: token.colorTextSecondary,
	}[tone];
	const asOf = status?.asOf ? new Date(status.asOf as string) : undefined;

	return (
		<Card
			size="small"
			hoverable
			onClick={onOpen}
			style={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				position: "relative",
				overflow: "hidden",
			}}
			styles={{ body: { flex: 1, display: "flex", flexDirection: "column" } }}
		>
			<LoadingBar active={isFetching} />
			<Flex vertical gap={4} style={{ flex: 1 }}>
				<Flex justify="space-between" align="center" gap={8}>
					<Typography.Text
						type="secondary"
						ellipsis={{ tooltip: signal.name }}
						style={{ minWidth: 0 }}
					>
						{signal.name}
					</Typography.Text>
					{isHealthSignal(signal) && (
						<SignalStateBadge state={status?.state as SignalState} />
					)}
				</Flex>
				<Typography.Text strong style={{ fontSize: 24, lineHeight: "32px" }}>
					{status ? formatMetric(status.value, status.unit) : "…"}
				</Typography.Text>
				<Typography.Text style={{ fontSize: 12, color: toneColor }}>
					{delta && status?.previousLabel
						? `${delta} vs ${status.previousLabel} (${formatMetric(status.previousValue, status.unit)})`
						: (status?.error ?? status?.reason ?? " ")}
				</Typography.Text>
				<Sparkline
					series={(status?.series as number[] | null) ?? []}
					unit={status?.unit}
					label={signal.name}
				/>
				{asOf && (
					<Tooltip title={format(asOf, "PPpp")}>
						<Typography.Text
							type="secondary"
							// Sits on the bottom row whatever the tile above it holds.
							style={{ fontSize: 12, marginTop: "auto" }}
						>
							Data as of {formatDistanceToNow(asOf, { addSuffix: true })}
						</Typography.Text>
					</Tooltip>
				)}
			</Flex>
		</Card>
	);
}

/**
 * The project's key numbers — each metric signal (users, cost, CloudWatch metrics) marked as a key
 * metric, as a stat tile. Shown under the health summary when the project has any.
 */
export function SignalKeyNumbers({
	signals,
	statuses,
	isFetching = false,
	onOpen,
}: {
	signals: Signal[];
	statuses: Record<string, SignalStatus> | undefined;
	/** True while statuses are being re-read; each tile shows a progress bar along its bottom. */
	isFetching?: boolean;
	onOpen: (signal: Signal) => void;
}) {
	const metrics = signals
		.filter((s) => s.kind === SignalKind.METRIC && s.keyMetric)
		.sort((a, b) => a.name.localeCompare(b.name));
	if (metrics.length === 0) return null;

	return (
		<BaseContainer title="Key numbers">
			<Row gutter={[12, 12]}>
				{metrics.map((m) => (
					<Col key={m.id} xs={24} sm={12} lg={8} xl={6}>
						<KeyNumberTile
							signal={m}
							status={statuses?.[m.id]}
							isFetching={isFetching}
							onOpen={() => onOpen(m)}
						/>
					</Col>
				))}
			</Row>
		</BaseContainer>
	);
}
